import React, { useState, useMemo } from 'react';
import { lookupUser, manageAccess, generateInvite } from '../API';
import { useToast } from '../Toast/ToastProvider';
import Spinner from '../Spinner/Spinner';
import { getFileDisplayName, DURATION_OPTIONS } from '../../util/GenericUtil';
import BottomSheet from '../BottomSheet/BottomSheet';
import './ShareModal.css';

function getAssessmentDate(file) {
    const match = file.key.match(/summary_(\d{8})/);
    if (!match) return file.key;
    const d = match[1];
    return `Assessment — ${d.slice(4, 6)}/${d.slice(6, 8)}/${d.slice(0, 4)}`;
}

export default function ShareModal({ isOpen, onClose, bloodTestFiles, assessmentFiles = [], preSelectedFileIndex, userEmail }) {
    const { showToast } = useToast();

    const allFiles = useMemo(() => {
        const blood = (bloodTestFiles || []).map((f, i) => ({ ...f, _type: 'blood', _origIndex: i }));
        const assess = (assessmentFiles || []).map(f => ({ ...f, _type: 'assessment' }));
        return [...blood, ...assess];
    }, [bloodTestFiles, assessmentFiles]);

    const initialToggles = useMemo(() => {
        if (allFiles.length === 0) return {};
        const toggles = {};
        allFiles.forEach((file, i) => {
            if (preSelectedFileIndex == null) {
                toggles[i] = true;
            } else {
                toggles[i] = file._type === 'blood' && file._origIndex === preSelectedFileIndex;
            }
        });
        return toggles;
    }, [allFiles, preSelectedFileIndex]);

    const [fileToggles, setFileToggles] = useState(initialToggles);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [selectedDuration, setSelectedDuration] = useState(null);
    const [loading, setLoading] = useState(false);
    const [inviteLink, setInviteLink] = useState(null);
    const [error, setError] = useState(null);

    React.useEffect(() => {
        if (isOpen) {
            setFileToggles(initialToggles);
            setRecipientEmail('');
            setSelectedDuration(null);
            setInviteLink(null);
            setError(null);
            setLoading(false);
        }
    }, [isOpen, initialToggles]);

    if (!isOpen) return null;

    const toggledCount = Object.values(fileToggles).filter(Boolean).length;
    const totalFiles = allFiles.length;
    const allToggled = toggledCount === totalFiles;

    const scope = allToggled ? 'all' : 'selective';
    // A blood entry can be a draw assembled from several panel sheets — share every
    // source key behind it, not just the one the group is identified by.
    const allowedResources = scope === 'selective'
        ? allFiles.filter((_, i) => fileToggles[i]).flatMap(f => (f.sourceKeys?.length ? f.sourceKeys : [f.key]))
        : null;

    const handleToggle = (index) => {
        setFileToggles(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const handleToggleAll = () => {
        const newVal = !allToggled;
        const toggles = {};
        allFiles.forEach((_, i) => { toggles[i] = newVal; });
        setFileToggles(toggles);
    };

    const bloodItems = allFiles.map((f, i) => [f, i]).filter(([f]) => f._type === 'blood');
    const assessItems = allFiles.map((f, i) => [f, i]).filter(([f]) => f._type === 'assessment');

    const handleShareByEmail = async () => {
        if (!recipientEmail || toggledCount === 0) return;
        setLoading(true);
        setError(null);
        try {
            if (userEmail && recipientEmail.toLowerCase() === userEmail.toLowerCase()) {
                setError('You cannot share with yourself');
                setLoading(false);
                return;
            }
            // Try direct grant if user exists, otherwise generate invite link
            let shared = false;
            try {
                const userResult = await lookupUser(recipientEmail);
                await manageAccess('grant', userResult.userId, {
                    granteeType: 'peer',
                    scope,
                    allowedResources,
                    expirationDays: selectedDuration,
                });
                shared = true;
            } catch {
                // User not found or lookup not permitted — generate invite link
                const result = await generateInvite(scope, allowedResources, selectedDuration);
                await navigator.clipboard.writeText(result.inviteUrl);
            }
            if (!shared) {
                showToast('User not found. Invite link copied to clipboard — share it with them.', 'info');
                onClose();
                return;
            }
            showToast(
                `Sharing ${allToggled ? 'all files' : `${toggledCount} files`}. Manage in Manage Sharing.`,
                'success'
            );
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateLink = async () => {
        if (toggledCount === 0) return;
        setLoading(true);
        setError(null);
        try {
            const result = await generateInvite(scope, allowedResources, selectedDuration);
            setInviteLink(result.inviteUrl);
            showToast('Invite link generated', 'success');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const copyLink = async () => {
        if (inviteLink) {
            try {
                await navigator.clipboard.writeText(inviteLink);
                showToast('Link copied to clipboard', 'info');
            } catch {
                showToast('Failed to copy link', 'error');
            }
        }
    };

    return (
        <BottomSheet onClose={onClose} className="share-modal" zIndex={9000}>
            {({ onClose: handleClose }) => (
                <>
                    <div className="share-modal-header">
                        <h2>Share Results</h2>
                        <button className="share-modal-close" onClick={handleClose}>&times;</button>
                    </div>

                    {/* File Toggles */}
                    <div className="share-files-section">
                        <div className="share-files-header">
                            <span className="share-files-label">Files to share</span>
                            <button className="share-toggle-all" onClick={handleToggleAll}>
                                {allToggled ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                        <div className="share-files-list">
                            {bloodItems.length > 0 && (
                                <>
                                    <div className="share-category-label">Blood Tests</div>
                                    {bloodItems.map(([file, idx]) => (
                                        <label key={idx} className="share-file-row">
                                            <input
                                                type="checkbox"
                                                checked={fileToggles[idx] || false}
                                                onChange={() => handleToggle(idx)}
                                            />
                                            <span className="share-file-name">{getFileDisplayName(file)}</span>
                                        </label>
                                    ))}
                                </>
                            )}
                            {assessItems.length > 0 && (
                                <>
                                    <div className="share-category-label">Assessments</div>
                                    {assessItems.map(([file, idx]) => (
                                        <label key={idx} className="share-file-row">
                                            <input
                                                type="checkbox"
                                                checked={fileToggles[idx] || false}
                                                onChange={() => handleToggle(idx)}
                                            />
                                            <span className="share-file-name">{getAssessmentDate(file)}</span>
                                        </label>
                                    ))}
                                </>
                            )}
                        </div>
                        <div className="share-scope-badge">
                            {allToggled ? 'All files' : `${toggledCount} of ${totalFiles} files`}
                        </div>
                    </div>

                    {/* Duration Selector */}
                    <div className="share-duration-section">
                        <span className="share-duration-label">Duration</span>
                        <div className="share-duration-options">
                            {DURATION_OPTIONS.map(opt => (
                                <button
                                    key={opt.label}
                                    className={`share-duration-btn ${selectedDuration === opt.days ? 'active' : ''}`}
                                    onClick={() => setSelectedDuration(opt.days)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Recipient */}
                    <div className="share-recipient-section">
                        <div className="share-email-row">
                            <input
                                type="email"
                                placeholder="Friend's email"
                                value={recipientEmail}
                                onChange={e => setRecipientEmail(e.target.value)}
                                className="share-email-input"
                            />
                            <button
                                className="share-add-btn"
                                onClick={handleShareByEmail}
                                disabled={loading || !recipientEmail || toggledCount === 0}
                            >
                                {loading ? <Spinner size={16} color="#fff" /> : 'Share'}
                            </button>
                        </div>

                        <div className="share-divider">
                            <span>or</span>
                        </div>

                        {!inviteLink ? (
                            <button
                                className="share-link-btn"
                                onClick={handleGenerateLink}
                                disabled={loading || toggledCount === 0}
                            >
                                {loading ? <Spinner size={16} color="#333" /> : 'Generate Invite Link'}
                            </button>
                        ) : (
                            <div className="share-link-result">
                                <input
                                    type="text"
                                    value={inviteLink}
                                    readOnly
                                    className="share-link-input"
                                />
                                <button className="share-copy-btn" onClick={copyLink}>
                                    Copy
                                </button>
                                <p className="share-link-note">
                                    This link expires in 24 hours and can only be used once.
                                </p>
                            </div>
                        )}
                    </div>

                    {error && <div className="share-error">{error}</div>}
                </>
            )}
        </BottomSheet>
    );
}
